/**
 * Self-serve sign-up (spec 2026-10-18). Pieces 4, 5 and 8 add the sign-up row, the routes and the switch to this
 * file; piece 3 owns the work email rule below.
 *
 * Decision 2: work email only. Personal and disposable email providers are refused at sign-up, by the sign-up screen
 * as the person types (auth/universal-login) and again by the Auth0 pre-user-registration Action trace-signup-policy
 * (auth/tenant), which cannot be bypassed. Neither can import this package (the screen bundles must stay small and
 * Actions run without dependencies), so both carry a copy of personalEmailDomains and their tests fail when a copy
 * differs from this list. Change the list here, then copy it into both.
 */
import { z } from 'zod';
import { INTELLIGENCE_LEVELS, intelligenceLevelSchema } from './admin.js';
import { billingCurrencySchema } from './billing.js';
/** Personal and disposable email providers refused at self-serve sign-up. Lowercase, sorted, unique. */
export const personalEmailDomains = Object.freeze([
    '10minutemail.com',
    '126.com',
    '163.com',
    '33mail.com',
    'aim.com',
    'anonaddy.me',
    'aol.com',
    'att.net',
    'bellsouth.net',
    'bigpond.com',
    'btinternet.com',
    'burnermail.io',
    'charter.net',
    'comcast.net',
    'cox.net',
    'discard.email',
    'dispostable.com',
    'duck.com',
    'earthlink.net',
    'emailondeck.com',
    'fakeinbox.com',
    'fastmail.com',
    'fastmail.fm',
    'free.fr',
    'frontier.com',
    'getairmail.com',
    'getnada.com',
    'gmail.com',
    'gmx.com',
    'gmx.de',
    'gmx.net',
    'googlemail.com',
    'grr.la',
    'guerrillamail.biz',
    'guerrillamail.com',
    'guerrillamail.de',
    'guerrillamail.info',
    'guerrillamail.net',
    'guerrillamail.org',
    'guerrillamailblock.com',
    'harakirimail.com',
    'hey.com',
    'hotmail.ca',
    'hotmail.co.uk',
    'hotmail.com',
    'hotmail.fr',
    'hushmail.com',
    'icloud.com',
    'inbox.com',
    'inboxkitten.com',
    'jetable.org',
    'juno.com',
    'laposte.net',
    'libero.it',
    'live.ca',
    'live.co.uk',
    'live.com',
    'live.fr',
    'mac.com',
    'mail.com',
    'mail.ru',
    'mail.tm',
    'mailbox.org',
    'mailcatch.com',
    'maildrop.cc',
    'mailinator.com',
    'mailinator.net',
    'mailnesia.com',
    'mailpoof.com',
    'mailsac.com',
    'me.com',
    'mintemail.com',
    'moakt.com',
    'mohmal.com',
    'mozmail.com',
    'msn.com',
    'mytemp.email',
    'nada.email',
    'naver.com',
    'netzero.net',
    'optonline.net',
    'orange.fr',
    'outlook.com',
    'outlook.fr',
    'pm.me',
    'posteo.de',
    'privaterelay.appleid.com',
    'proton.me',
    'protonmail.ch',
    'protonmail.com',
    'qq.com',
    'rocketmail.com',
    'rogers.com',
    'sbcglobal.net',
    'sharklasers.com',
    'shaw.ca',
    'simplelogin.com',
    'sky.com',
    'spam4.me',
    'spamgourmet.com',
    'sympatico.ca',
    't-online.de',
    'telus.net',
    'temp-mail.org',
    'tempail.com',
    'tempmail.com',
    'tempmail.dev',
    'tempmailo.com',
    'tempr.email',
    'throwawaymail.com',
    'trashmail.com',
    'trashmail.de',
    'tuta.io',
    'tutanota.com',
    'verizon.net',
    'videotron.ca',
    'wanadoo.fr',
    'web.de',
    'windstream.net',
    'yahoo.ca',
    'yahoo.co.in',
    'yahoo.co.jp',
    'yahoo.co.uk',
    'yahoo.com',
    'yahoo.com.mx',
    'yahoo.fr',
    'yandex.com',
    'yandex.ru',
    'ymail.com',
    'yopmail.com',
    'yopmail.fr',
    'yopmail.net',
    'zoho.com',
]);
const PERSONAL = new Set(personalEmailDomains);
/** Two or more dot-separated labels of letters, digits and inner hyphens: a domain an address can use. */
const DOMAIN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/;
/**
 * The lowercased domain of an email address (the part after the last @), or null when the text is not an address:
 * nothing before the @, or a domain that is not two or more well-formed labels.
 */
export function emailDomainOf(email) {
    if (typeof email !== 'string')
        return null;
    const value = email.trim();
    const at = value.lastIndexOf('@');
    if (at <= 0 || at === value.length - 1)
        return null;
    const domain = value.slice(at + 1).toLowerCase();
    return DOMAIN.test(domain) ? domain : null;
}
/**
 * True when the domain is a listed personal or disposable provider, or any subdomain of one (inbox.mailinator.com),
 * ignoring case, surrounding spaces and one trailing dot. Walks the domain's suffixes, so the check costs one set
 * lookup per label.
 */
export function isPersonalEmailDomain(domain) {
    if (typeof domain !== 'string')
        return false;
    let candidate = domain.trim().toLowerCase().replace(/\.$/, '');
    while (candidate.includes('.')) {
        if (PERSONAL.has(candidate))
            return true;
        candidate = candidate.slice(candidate.indexOf('.') + 1);
    }
    return false;
}
/**
 * The reasons trace-signup-policy refuses a sign-up with (api.access.deny): a personal or disposable email domain,
 * or a sign-up through any application other than the account hub. The sign-up screens map both to plain copy.
 */
export const SIGNUP_REFUSAL_CODES = ['personal_email_domain', 'signup_not_allowed'];
/*
 * Piece 4 (still 0.20.0): the automatic setup after the card. A sign-up records the setup the founder chose and moves
 * through SELF_SERVE_SIGNUP_STATES while platform-api runs SELF_SERVE_STEPS in order (contract section 4).
 */
/** The product and the catalog plan a self-serve sign-up buys. */
export const SELF_SERVE_PRODUCT_KEY = 'enterprise';
export const SELF_SERVE_PLAN_KEY = 'trace-enterprise';
/** The seven features the pricing page sells, as Enterprise modules. The assistant is always on and never sold. */
export const SELF_SERVE_MODULE_KEYS = [
    'enterprise.quoting',
    'enterprise.negotiation',
    'enterprise.booking',
    'enterprise.tracking',
    'enterprise.intake',
    'enterprise.rate_intelligence',
    'enterprise.communications',
];
export const SELF_SERVE_ALWAYS_ON_MODULE_KEYS = ['enterprise.assistant'];
export const SELF_SERVE_MAX_CARRIER_CONNECTIONS = 30;
export const SELF_SERVE_SHIPMENTS_MIN = 1;
export const SELF_SERVE_SHIPMENTS_MAX = 150;
/**
 * The setup a founder chose on the pricing page and confirmed on the hub's Start page: the modules switched on, the
 * intelligence level, and the shipments a month (an estimate only: it seeds the suggested spend cap). Negotiation and
 * booking work on quotes, so they need quoting.
 */
export const selfServeSetupSchema = z
    .strictObject({
    modules: z.array(z.enum(SELF_SERVE_MODULE_KEYS)).min(1).max(7),
    intelligenceLevel: intelligenceLevelSchema,
    shipmentsPerMonth: z.number().int().min(SELF_SERVE_SHIPMENTS_MIN).max(SELF_SERVE_SHIPMENTS_MAX),
})
    .superRefine((setup, ctx) => {
    if (new Set(setup.modules).size !== setup.modules.length) {
        ctx.addIssue({ code: 'custom', path: ['modules'], message: 'each module at most once' });
    }
    for (const needsQuoting of ['enterprise.negotiation', 'enterprise.booking']) {
        if (setup.modules.includes(needsQuoting) && !setup.modules.includes('enterprise.quoting')) {
            ctx.addIssue({
                code: 'custom',
                path: ['modules'],
                message: `${needsQuoting} needs enterprise.quoting`,
            });
        }
    }
});
/** What a missing or unreadable setup becomes (contract D22): Full desk, Steady, 80 shipments, 0 carriers. */
export const SELF_SERVE_DEFAULT_SETUP = {
    modules: [
        'enterprise.quoting',
        'enterprise.negotiation',
        'enterprise.booking',
        'enterprise.tracking',
        'enterprise.intake',
    ],
    intelligenceLevel: 'steady',
    shipmentsPerMonth: 80,
};
/** A sign-up's states (contract section 4.2). An open sign-up holds its person and its email domain. */
export const SELF_SERVE_SIGNUP_STATES = [
    'awaiting_card',
    'provisioning',
    'needs_attention',
    'complete',
    'expired',
];
export const SELF_SERVE_OPEN_STATES = ['awaiting_card', 'provisioning', 'needs_attention'];
/** The setup steps, in the order they run (contract section 4.3). */
export const SELF_SERVE_STEPS = [
    'organization',
    'identity_provider',
    'founder',
    'contract',
    'payment_method',
    'modules',
    'enterprise',
];
/** The third failure in a row needs staff; the first two wait 60 and 300 seconds (contract section 4.4). */
export const SELF_SERVE_MAX_FAILURES = 3;
export const SELF_SERVE_RETRY_DELAYS_MS = [60_000, 300_000];
/** A sign-up still waiting for its card after this many days expires, freeing the person and the domain. */
export const SELF_SERVE_AWAITING_CARD_TTL_DAYS = 7;
/** One sign-up as Trace staff see it (Trace Admin > Billing, "Sign-ups needing attention"). */
export const adminSelfServeSignupSchema = z.object({
    id: z.uuid(),
    state: z.enum(SELF_SERVE_SIGNUP_STATES),
    companyName: z.string(),
    email: z.string(),
    emailDomain: z.string(),
    setup: selfServeSetupSchema,
    carrierConnections: z.number().int(),
    spendCapMicros: z.number().int(),
    currency: billingCurrencySchema,
    planVersionId: z.uuid(),
    completedSteps: z.array(z.enum(SELF_SERVE_STEPS)),
    failedStep: z.enum(SELF_SERVE_STEPS).nullable(),
    failureCount: z.number().int(),
    /** "<reason>: <message>" of the last failure; never a secret or a provider's own text. */
    lastError: z.string().nullable(),
    nextAttemptAt: z.iso.datetime().nullable(),
    organizationId: z.uuid().nullable(),
    contractId: z.uuid().nullable(),
    /** Enterprise's own id of the organization it created, as text. */
    enterpriseOrganizationRef: z.string().nullable(),
    cardSavedAt: z.iso.datetime().nullable(),
    needsAttentionAt: z.iso.datetime().nullable(),
    completedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const adminSelfServeSignupListQuerySchema = z.strictObject({
    state: z.enum(SELF_SERVE_SIGNUP_STATES).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const adminSelfServeSignupListSchema = z.object({ items: z.array(adminSelfServeSignupSchema) });
/** Staff retry of a sign-up needing attention, always with a reason (audited). */
export const selfServeRetrySchema = z.strictObject({ reason: z.string().min(3).max(500) });
/** The self-serve routes (pieces 5 and 8 add theirs to this object). */
export const PLATFORM_SELF_SERVE_API = {
    /** GET, staff with staff.billing.read: sign-ups, newest change first, optionally of one state. */
    adminSignups: '/v1/admin/billing/self-serve-signups',
    /** POST, staff with staff.billing.manage: run a sign-up needing attention again (409 signup_not_retryable otherwise). */
    adminRetrySignup: (signupId) => `/v1/admin/billing/self-serve-signups/${encodeURIComponent(signupId)}/retry`,
    /** Piece 5: GET, public. Whether self-serve sign-up is open (the pricing site's function reads it). */
    status: '/v1/public/self-serve',
    /** Piece 5: POST, the signed-in founder. The price of a setup, and the spend cap suggested for it. */
    estimate: '/v1/self-serve/estimate',
    /** Piece 5: POST, the signed-in founder with a verified work email. The sign-up and its Checkout page. */
    signups: '/v1/self-serve/signups',
    /** Piece 5: GET, the founder's open or complete sign-up (404 when none). */
    currentSignup: '/v1/self-serve/signups/current',
    /** Piece 5: GET, one of the founder's own sign-ups (another person's answers 404). */
    signup: (signupId) => `/v1/self-serve/signups/${encodeURIComponent(signupId)}`,
    /**
     * Contracts 0.21.0 (piece 8): GET (staff.billing.read) and PUT (staff.billing.manage, with a reason) the self-serve
     * switch in Trace Admin > Billing. PUT answers selfServeSettingsSchema; an unchanged request records nothing.
     */
    adminSettings: '/v1/admin/billing/self-serve',
};
/** The self-serve event types, all in PLATFORM_EVENT_TYPES too (pieces 4 and 5 emit them; piece 8 adds its own). */
export const SELF_SERVE_EVENT_TYPES = [
    'platform.self_serve.signup_created',
    'platform.self_serve.card_saved',
    'platform.self_serve.signup_completed',
    'platform.self_serve.signup_needs_attention',
    // Contracts 0.21.0 (piece 8): Trace staff turned self-serve sign-up on or off (data: { enabled }).
    'platform.self_serve.switch_changed',
];
/* ---------------------------------------------------------------------------------------------- */
/* Start your trial (spec 2026-10-18 piece 5)                                                      */
/* ---------------------------------------------------------------------------------------------- */
/** The version of the terms a founder accepts on the Start your trial page; new terms are a new version. */
export const SELF_SERVE_TERMS_VERSION = '2026-10-18';
/** Where those terms are published (linked next to the checkbox). */
export const SELF_SERVE_TERMS_URL = 'https://trace.io/terms';
/**
 * The trace.io pricing page's feature ids (builder.ts FEATURES) and the Enterprise module each one stands for. The page
 * carries its setup to the hub as ?setup=s<shipments>.l<level 0-3>.c<carriers>.f<feature ids, comma separated>
 * (builder.ts encodeSetup).
 */
export const SITE_FEATURE_MODULES = {
    quote: 'enterprise.quoting',
    negotiate: 'enterprise.negotiation',
    book: 'enterprise.booking',
    track: 'enterprise.tracking',
    intake: 'enterprise.intake',
    rates: 'enterprise.rate_intelligence',
    comms: 'enterprise.communications',
};
// The page writes lowercase letters, digits, dots and commas only; anything else is not its code.
const ENCODED_SETUP = /^s(\d{1,9})\.l(\d)\.c(\d{1,9})\.f([a-z,]{0,200})$/;
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
/**
 * Reads the pricing page's setup code. It comes from a URL anyone can edit, so it is never trusted and never throws:
 * shipments are clamped to 1..150, carrier connections to 0..30 and the level index to the four levels (0 Steady, 1
 * Seasoned, 2 Strategist, 3 Mastermind); unknown feature ids are dropped; quoting is added when negotiation or booking
 * is chosen (both need it). A missing or malformed code, or one with no known feature, is the default setup (Full desk,
 * Steady, 80 shipments) with no carriers and fellBack true, so the page can say it could not read the link.
 */
export function decodeSelfServeSetup(text) {
    const match = text === null ? null : ENCODED_SETUP.exec(text);
    const chosen = new Set();
    for (const id of match?.[4]?.split(',') ?? []) {
        const moduleKey = SITE_FEATURE_MODULES[id];
        if (moduleKey)
            chosen.add(moduleKey);
    }
    if (!match || chosen.size === 0) {
        return {
            setup: { ...SELF_SERVE_DEFAULT_SETUP, modules: [...SELF_SERVE_DEFAULT_SETUP.modules] },
            carrierConnections: 0,
            fellBack: true,
        };
    }
    if (chosen.has('enterprise.negotiation') || chosen.has('enterprise.booking'))
        chosen.add('enterprise.quoting');
    return {
        setup: {
            // Catalog order, so the same choice always reads the same.
            modules: SELF_SERVE_MODULE_KEYS.filter((key) => chosen.has(key)),
            intelligenceLevel: INTELLIGENCE_LEVELS[clamp(Number(match[2]), 0, INTELLIGENCE_LEVELS.length - 1)],
            shipmentsPerMonth: clamp(Number(match[1]), SELF_SERVE_SHIPMENTS_MIN, SELF_SERVE_SHIPMENTS_MAX),
        },
        carrierConnections: clamp(Number(match[3]), 0, SELF_SERVE_MAX_CARRIER_CONNECTIONS),
        fellBack: false,
    };
}
/** GET /v1/public/self-serve: whether self-serve sign-up is open. Public; the pricing site reads it server side. */
export const selfServeStatusSchema = z.object({ enabled: z.boolean() });
/** POST /v1/self-serve/estimate: the setup the page shows and the carrier connections bought with it. */
export const selfServeEstimateRequestSchema = z.strictObject({
    setup: selfServeSetupSchema,
    carrierConnections: z.number().int().min(0).max(SELF_SERVE_MAX_CARRIER_CONNECTIONS),
});
/**
 * The price of a setup on the newest published Trace Enterprise version (decision D17): the base and the carrier
 * connections billed each month, the trial, the tokens the setup is estimated to use a month and their cost on the
 * curve, and the spend cap suggested from them (1.5 times that cost, rounded up to the next 100 of the currency, never
 * under the minimum). The hub never prices anything itself.
 */
export const selfServeEstimateSchema = z.object({
    planVersionId: z.uuid(),
    currency: billingCurrencySchema,
    basePriceMicros: z.number().int(),
    carrierConnectionPriceMicros: z.number().int(),
    maxCarrierConnections: z.number().int(),
    recurringMicros: z.number().int(),
    trialDays: z.number().int(),
    trialTokenBudget: z.number().int().nullable(),
    estimatedMillitokens: z.number().int(),
    estimatedTokenCostMicros: z.number().int(),
    minimumSpendCapMicros: z.number().int(),
    suggestedSpendCapMicros: z.number().int(),
    termsVersion: z.literal(SELF_SERVE_TERMS_VERSION),
    termsUrl: z.url(),
});
/** POST /v1/self-serve/signups: what the founder chose, the cap they set, and the terms they accepted. */
export const selfServeSignupCreateSchema = z.strictObject({
    companyName: z.string().trim().min(2).max(120),
    setup: selfServeSetupSchema,
    carrierConnections: z.number().int().min(0).max(SELF_SERVE_MAX_CARRIER_CONNECTIONS),
    spendCapMicros: z.number().int().positive().max(1_000_000_000_000),
    termsVersion: z.literal(SELF_SERVE_TERMS_VERSION),
});
/**
 * A sign-up as its founder sees it. delayed: a step failed at least once since the last success, or the sign-up waits
 * for Trace staff (needs_attention), so the page says the setup is taking longer. destination: where the founder goes,
 * only once the sign-up is complete.
 */
export const selfServeSignupSchema = z.object({
    id: z.uuid(),
    state: z.enum(SELF_SERVE_SIGNUP_STATES),
    companyName: z.string(),
    setup: selfServeSetupSchema,
    carrierConnections: z.number().int(),
    spendCapMicros: z.number().int(),
    currency: billingCurrencySchema,
    completedSteps: z.array(z.enum(SELF_SERVE_STEPS)),
    totalSteps: z.literal(7),
    delayed: z.boolean(),
    destination: z.object({ productKey: z.literal('enterprise'), organizationId: z.uuid() }).nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/** 201 of POST /v1/self-serve/signups: the sign-up and the provider page where the card is saved (nothing charged). */
export const selfServeCheckoutSchema = z.object({ signup: selfServeSignupSchema, checkoutUrl: z.url() });
/**
 * The self-serve switch (spec 2026-10-18 decision 5, contracts 0.21.0): platform_settings.selfServeEnabled. It is what
 * GET /v1/public/self-serve answers, and what POST /v1/self-serve/estimate and POST /v1/self-serve/signups check
 * (403 self_serve_disabled while off); sign-ups already past the card finish either way. updatedBy is null until a
 * staff member first changes it in Trace Admin (and when that person's user no longer exists); updatedAt and
 * updatedBy are both null only when the settings row is missing, which platform-api answers as off.
 */
export const selfServeSettingsSchema = z.object({
    enabled: z.boolean(),
    updatedAt: z.iso.datetime().nullable(),
    updatedBy: z.object({ id: z.uuid(), displayName: z.string() }).nullable(),
});
/** PUT the self-serve switch: on or off, always with the reason recorded in the audit log (staff.billing.manage). */
export const selfServeSettingsChangeSchema = z.strictObject({
    enabled: z.boolean(),
    reason: z.string().min(3).max(500),
});
//# sourceMappingURL=self-serve.js.map